import React, { useCallback, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useAgentIdleStateController } from '@features/Admin/Agent/hooks/useAgentIdleStateController';
import { AgentAdvancedSettingsTabs } from './AgentAdvancedSettingsModal/AgentAdvancedSettingsTabs';
import { createExpandedSettingsState } from './AgentAdvancedSettingsModal/constants';
import { UnsavedChangesDialog } from './AgentAdvancedSettingsModal/UnsavedChangesDialog';
import { ValidationDialog } from './AgentAdvancedSettingsModal/ValidationDialog';
import { useAdvancedSettingsCategoryNavigation } from './AgentAdvancedSettingsModal/useAdvancedSettingsCategoryNavigation';
import { useAdvancedSettingsFormHydration } from './AgentAdvancedSettingsModal/useAdvancedSettingsFormHydration';
import { useAgentAdvancedSettingsTools } from './AgentAdvancedSettingsModal/useAgentAdvancedSettingsTools';
import { useAdvancedSettingsUnsavedGuard } from './AgentAdvancedSettingsModal/useAdvancedSettingsUnsavedGuard';
import type {
  AgentAdvancedSettingsModalProps,
  ExpandedSettingsState,
} from './AgentAdvancedSettingsModal/types';

export type { AgentAdvancedSettingsModalProps } from './AgentAdvancedSettingsModal/types';

const stripUndefined = <T,>(value: T): T => JSON.parse(JSON.stringify(value ?? null));

const AgentAdvancedSettingsModal: React.FC<AgentAdvancedSettingsModalProps> = ({
  open,
  onClose,
  agent,
  agents,
  campaignId,
  campaignTools = [],
  campaignName = '',
  companyName = '',
  formMethods,
  onSave,
  refetch,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const [expandedSettings, setExpandedSettings] = useState<ExpandedSettingsState>(
    createExpandedSettingsState(false),
  );
  const idleStateController = useAgentIdleStateController();
  const { formState, getValues, reset, trigger, watch } = formMethods;
  const { errors, defaultValues } = formState;
  const formValues = watch();
  const isFormDirty = useMemo(
    () => !isEqual(stripUndefined(formValues), stripUndefined(defaultValues)),
    [defaultValues, formValues],
  );
  const isDirty = open && (isFormDirty || idleStateController.isDirty);

  const { agentNonMcpTools, allAgentTools, isLoadingAgentTools, refetchAgentTools } =
    useAgentAdvancedSettingsTools({ open, agent, campaignTools });

  const setSettingsExpanded = useCallback((key: keyof ExpandedSettingsState, expanded: boolean) => {
    setExpandedSettings((previous) => ({ ...previous, [key]: expanded }));
  }, []);

  const { isHydrated } = useAdvancedSettingsFormHydration({
    open,
    agent,
    agentNonMcpTools,
    isLoadingAgentTools,
    formMethods,
    hydrateIdleState: idleStateController.hydrate,
    setExpandedSettings,
  });

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (isReadOnly) return false;

    const isValid = await trigger();
    const validatedIdleState = idleStateController.validate();
    const hasValidationErrors = !isValid || Object.keys(errors).length > 0 || !validatedIdleState;
    if (hasValidationErrors) return false;

    const saved = await onSave(validatedIdleState);
    if (saved) {
      reset(getValues());
      idleStateController.commit();
    }

    return saved;
  }, [errors, getValues, idleStateController, isReadOnly, onSave, reset, trigger]);

  const categoryNavigation = useAdvancedSettingsCategoryNavigation({
    open,
    agentId: agent.id,
    isDirty,
    formMethods,
    validateIdleState: idleStateController.validate,
  });

  const refetchIntegrations = useCallback(() => {
    refetchAgentTools();
    refetch();
  }, [refetch, refetchAgentTools]);

  const unsavedGuard = useAdvancedSettingsUnsavedGuard({
    isDirty,
    onClose,
    onSave: handleSave,
  });

  return (
    <Dialog
      open={open}
      onClose={unsavedGuard.requestClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            height: '70vh',
            maxHeight: '70vh',
          },
        },
      }}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('adminMainPage.operation.advancedSettings')} - {agent.name}
          </Typography>
          <IconButton
            onClick={unsavedGuard.requestClose}
            size="small"
            aria-label={t('adminMainPage.agent.advancedSettingsTabs.close')}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          paddingY: 0,
        }}>
        <Box
          sx={{
            padding: 0,
            width: '100%',
            minHeight: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
          <AgentAdvancedSettingsTabs
            activeCategory={categoryNavigation.activeCategory}
            onCategoryChange={categoryNavigation.handleCategoryChange}
            agent={agent}
            agents={agents}
            campaignId={campaignId}
            campaignName={campaignName}
            companyName={companyName}
            formMethods={formMethods}
            isReadOnly={isReadOnly}
            expandedSettings={expandedSettings}
            onExpandedChange={setSettingsExpanded}
            idleState={{
              draft: idleStateController.draft,
              errors: idleStateController.errors,
              isReadOnly,
              onDraftChange: idleStateController.update,
            }}
            allAgentTools={allAgentTools}
            isLoadingAgentTools={!isHydrated}
            refetchIntegrations={refetchIntegrations}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {!isReadOnly && (
          <Button
            data-testid="agent-advanced-settings-save"
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!isDirty}>
            {t('adminMainPage.operation.save')}
          </Button>
        )}
      </DialogActions>
      <ValidationDialog
        open={categoryNavigation.showValidationDialog}
        onClose={categoryNavigation.closeValidationDialog}
      />
      <UnsavedChangesDialog
        open={unsavedGuard.showUnsavedChangesDialog}
        isReadOnly={isReadOnly}
        isSaving={unsavedGuard.isUnsavedChangesSaving}
        onCancel={unsavedGuard.handleCancelUnsavedChanges}
        onDiscard={unsavedGuard.handleDiscardUnsavedChanges}
        onSave={() => void unsavedGuard.handleSaveFromUnsavedChangesDialog()}
      />
    </Dialog>
  );
};

export default AgentAdvancedSettingsModal;
